/** Derivations over the API contract types that both client and server need. */

import { formatCondition } from "./conditions.js";
import type { Alert, Basket, Settings, Token, WatchStatus } from "./contracts.js";
import { fmt, multiplier, pct, price, usd } from "./format.js";
import {
  type BuyRung,
  basketStatus,
  calcLadder,
  isFloorBroken,
  type LadderInput,
  type LadderResult,
  nextPendingRung,
} from "./ladder.js";

export function toLadderInput(b: Basket): LadderInput {
  const buyFills: Record<number, { status: "filled" | "skipped"; price: number | null; qty: number | null }> =
    {};
  for (const f of b.buyFills) buyFills[f.rungIndex] = { status: f.status, price: f.price, qty: f.qty };
  const sellFills: Record<number, { price: number; qty: number }> = {};
  for (const f of b.sellFills) sellFills[f.tpIndex] = { price: f.price, qty: f.qty };
  return {
    budgetUsd: b.budgetUsd,
    startPrice: b.startPrice,
    floor: b.floor,
    rungCount: b.rungCount,
    spacing: b.spacing,
    dist: b.dist,
    feePct: b.feePct,
    exitMode: b.exitMode,
    target: b.target,
    tp: b.tp,
    moonPct: b.moonPct,
    buyFills,
    sellFills,
  };
}

export function calcBasket(b: Basket): LadderResult {
  return calcLadder(toLadderInput(b));
}

/** Watchlist status derived from the token's baskets. */
export function tokenWatchStatus(baskets: readonly Basket[]): WatchStatus {
  if (baskets.some((b) => b.status === "active")) return "koszyk aktywny";
  if (baskets.some((b) => b.status === "waiting")) return "czekam na poziom";
  if (baskets.some((b) => b.status === "closed" && b.sellFills.length > 0)) return "wyszedłem";
  return "obserwuję";
}

/** The basket a token row should link to: active > waiting > most recently closed. */
export function primaryBasket(baskets: readonly Basket[]): Basket | null {
  return (
    baskets.find((b) => b.status === "active") ??
    baskets.find((b) => b.status === "waiting") ??
    [...baskets]
      .filter((b) => b.status === "closed")
      .sort((a, z) => (z.closedAt ?? "").localeCompare(a.closedAt ?? ""))[0] ??
    null
  );
}

export type AttentionItem =
  | { readonly kind: "floor"; readonly basket: Basket; readonly token: Token; readonly distancePct: number }
  | {
      readonly kind: "start";
      readonly basket: Basket;
      readonly token: Token;
      readonly distancePct: number;
      readonly indicatorMet: boolean | null;
    }
  | {
      readonly kind: "near_rung";
      readonly basket: Basket;
      readonly token: Token;
      readonly rung: BuyRung;
      readonly distancePct: number;
    };

/**
 * Dashboard "Koszyki wymagające uwagi". `indicatorMet` lets the caller pass the evaluation of
 * the basket's start indicator (null = unknown).
 */
export function attentionList(
  baskets: readonly Basket[],
  tokensById: ReadonlyMap<string, Token>,
  settings: Pick<Settings, "nearRungPct">,
  indicatorMet: (b: Basket) => boolean | null = () => null,
): AttentionItem[] {
  const out: AttentionItem[] = [];
  for (const basket of baskets) {
    const token = tokensById.get(basket.tokenId);
    if (!token || basket.status === "closed") continue;
    const p = token.priceUsd;
    if (isFloorBroken(basket, p) && p != null) {
      out.push({ kind: "floor", basket, token, distancePct: ((p - basket.floor) / basket.floor) * 100 });
      continue;
    }
    if (basket.status === "waiting") {
      if (basket.startCond === "manual" || basket.startCond === "date" || basket.startX == null || p == null)
        continue;
      out.push({
        kind: "start",
        basket,
        token,
        distancePct: ((p - basket.startX) / basket.startX) * 100,
        indicatorMet: basket.startCond === "indicator" ? indicatorMet(basket) : null,
      });
      continue;
    }
    if (p == null) continue;
    const next = nextPendingRung(calcBasket(basket));
    if (!next) continue;
    const distancePct = ((next.price - p) / p) * 100;
    if (Math.abs(distancePct) < settings.nearRungPct)
      out.push({ kind: "near_rung", basket, token, rung: next, distancePct });
  }
  const order = { floor: 0, near_rung: 1, start: 2 } as const;
  return out.sort((a, z) => order[a.kind] - order[z.kind]);
}

export type AlertDescription = {
  /** e.g. "INJ · szczebel 3" */
  readonly target: string;
  /** e.g. "16,24 USD (−3,9 % od ceny)" */
  readonly threshold: string;
  /** trigger price when the alert is price-based */
  readonly thresholdPrice: number | null;
  readonly typeLabel: string;
  readonly tone: "amber" | "blue" | "red" | "green" | "gray";
};

export const ALERT_TYPE_LABEL: Record<Alert["kind"], string> = {
  entry_rung: "Szczebel wejścia",
  start_condition: "Warunek startu",
  floor: "Podłoga",
  exit_rung: "Szczebel wyjścia",
  indicator: "Wskaźnik",
  regime_change: "Reżim rynku",
  trailing_stop: "Trailing stop",
  exit_trigger: "Warunek wyjścia",
};

export function describeAlert(
  alert: Alert,
  basket: Basket | null,
  token: Token | null,
  result: LadderResult | null,
): AlertDescription {
  const typeLabel = ALERT_TYPE_LABEL[alert.kind];
  const p = token?.priceUsd ?? null;
  const fromNow = (x: number) => (p != null && p > 0 ? ` (${pct(((x - p) / p) * 100)} od ceny)` : "");
  const sym = token?.symbol ?? "?";
  switch (alert.kind) {
    case "entry_rung": {
      const rung = result?.buys[alert.rungIndex ?? -1] ?? null;
      return {
        typeLabel,
        tone: "amber",
        target: `${sym} · szczebel ${(alert.rungIndex ?? 0) + 1}`,
        threshold: rung ? `${price(rung.price)}${fromNow(rung.price)} · ${usd(rung.amount)}` : "—",
        thresholdPrice: rung?.price ?? null,
      };
    }
    case "exit_rung": {
      const tp = result?.sells[alert.rungIndex ?? -1] ?? null;
      return {
        typeLabel,
        tone: "green",
        target: `${sym} · TP${(alert.rungIndex ?? 0) + 1}`,
        threshold: tp
          ? `${price(tp.price)} (${multiplier(tp.mult)} od śr. ${result?.realAvg != null ? "zreal." : "planu"})`
          : "—",
        thresholdPrice: tp?.price ?? null,
      };
    }
    case "floor":
      return {
        typeLabel,
        tone: "red",
        target: `${sym} · koszyk`,
        threshold: basket ? `${price(basket.floor)}${fromNow(basket.floor)}` : "—",
        thresholdPrice: basket?.floor ?? null,
      };
    case "start_condition": {
      if (!basket)
        return { typeLabel, tone: "blue", target: `${sym} · koszyk`, threshold: "—", thresholdPrice: null };
      const parts: string[] = [];
      if (basket.startCond === "price" || basket.startCond === "indicator")
        parts.push(`cena ≤ ${price(basket.startX)}`);
      if (basket.startCond === "indicator" && basket.startIndicator)
        parts.push(formatCondition(basket.startIndicator));
      if (basket.startCond === "date" && basket.startDate) parts.push(`data ${basket.startDate}`);
      return {
        typeLabel,
        tone: "blue",
        target: `${sym} · koszyk`,
        threshold: parts.join(" i ") || "ręcznie",
        thresholdPrice: basket.startX,
      };
    }
    case "indicator":
      return {
        typeLabel,
        tone: "gray",
        target: alert.condition ? (formatCondition(alert.condition).split(" ")[0] ?? "wskaźnik") : "wskaźnik",
        threshold: alert.condition ? formatCondition(alert.condition) : "—",
        thresholdPrice: null,
      };
    case "regime_change":
      return {
        typeLabel,
        tone: "gray",
        target: "zmiana etykiety",
        threshold: "dowolna zmiana",
        thresholdPrice: null,
      };
    case "trailing_stop": {
      const stopPrice =
        basket?.trailingPct != null && basket.trailingHigh != null
          ? basket.trailingHigh * (1 - basket.trailingPct / 100)
          : null;
      return {
        typeLabel,
        tone: "green",
        target: `${sym} · koszyk`,
        threshold:
          stopPrice != null && basket?.trailingHigh != null && basket.trailingPct != null
            ? `${pct(-basket.trailingPct)} od szczytu ${price(basket.trailingHigh)} → ${price(stopPrice)}`
            : "—",
        thresholdPrice: stopPrice,
      };
    }
    case "exit_trigger":
      return {
        typeLabel,
        tone: "green",
        target: `${sym} · koszyk`,
        threshold: basket?.exitTrigger ? formatCondition(basket.exitTrigger) : "—",
        thresholdPrice: null,
      };
  }
}

/** Journal body for a recorded buy fill, e.g. "Kupno INJ, szczebel 2 — 15,70 × 19,10 USD = 299,87 USD (1 157 PLN, kurs 3,86)." */
export function fillJournalBody(
  token: Pick<Token, "symbol">,
  rungIndex: number,
  qty: number,
  fillPrice: number,
  fx: { rate: number; date: string } | null,
): string {
  const total = qty * fillPrice;
  const plnPart = fx ? ` (${fmt(total * fx.rate, 0)} PLN, kurs ${fmt(fx.rate, 2)} z ${fx.date})` : "";
  return `Kupno ${token.symbol}, szczebel ${rungIndex + 1} — ${fmt(qty, 2)} × ${price(fillPrice)} = ${usd(total, 2)}${plnPart}.`;
}

export function sellJournalBody(
  token: Pick<Token, "symbol">,
  tpIndex: number,
  qty: number,
  fillPrice: number,
  fx: { rate: number; date: string } | null,
): string {
  const total = qty * fillPrice;
  const plnPart = fx ? ` (${fmt(total * fx.rate, 0)} PLN, kurs ${fmt(fx.rate, 2)} z ${fx.date})` : "";
  return `Sprzedaż ${token.symbol}, TP${tpIndex + 1} — ${fmt(qty, 2)} × ${price(fillPrice)} = ${usd(total, 2)}${plnPart}.`;
}

export { basketStatus };
